import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { useEffect, type ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

/**
 * Shared base for the suspend / reactivate dialogs (AUT-124). A required
 * free-text `reason` (max 500, mirrors backend `SetUserStatusSchema`) is
 * collected via RHF + Zod and passed to `onConfirm(reason)`. The parent
 * owns the async lifecycle (step-up + mutation) via `loading`.
 *
 * The `reason` is forwarded to the audit row server-side — it is NOT PII
 * and is required for forensics, hence the non-empty validation.
 */
const MAX_REASON = 500;

const buildSchema = (requiredKey: string, maxKey: string) =>
  z.object({
    reason: z.string().trim().min(1, requiredKey).max(MAX_REASON, maxKey),
  });

type ReasonForm = { reason: string };

export interface StatusReasonDialogProps {
  open: boolean;
  title: string;
  description: string;
  reasonLabel: string;
  reasonRequiredKey: string;
  reasonMaxKey: string;
  confirmLabel: string;
  /** Confirm button colour variant. */
  color: 'error' | 'success';
  loading?: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

export const StatusReasonDialog = ({
  open,
  title,
  description,
  reasonLabel,
  reasonRequiredKey,
  reasonMaxKey,
  confirmLabel,
  color,
  loading = false,
  onConfirm,
  onCancel,
}: StatusReasonDialogProps): ReactElement => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<ReasonForm>({
    resolver: zodResolver(buildSchema(reasonRequiredKey, reasonMaxKey)),
    mode: 'onChange',
    defaultValues: { reason: '' },
  });

  useEffect(() => {
    if (open) reset({ reason: '' });
  }, [open, reset]);

  const submit = ({ reason }: ReasonForm): void => {
    if (loading) return;
    onConfirm(reason.trim());
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <form onSubmit={handleSubmit(submit)}>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{description}</DialogContentText>
          <TextField
            {...register('reason')}
            fullWidth
            multiline
            minRows={2}
            size="small"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            disabled={loading}
            label={reasonLabel}
            error={Boolean(errors.reason)}
            helperText={errors.reason ? t(errors.reason.message ?? '') : ' '}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color={color}
            disabled={loading || !isValid}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
